<template>
  <div class="container">
    <!-- 中间面板 -->
    <div class="game-center-panel">
      <div class="game-header">
        <button class="control-btn" @click="prevLevel">上一关</button>
        <div class="level-display">
          <span class="level-text">第</span>
          <span class="level-number" @click="showLevelInput">{{ currentLevel }}</span>
          <span class="level-text">关</span>
        </div>
        <button class="control-btn" @click="nextLevel">下一关</button>
      </div>

      <div class="controls">
        <button class="control-btn" @click="resetGame">重新开始</button>
        <button class="control-btn" @click="undoLastMove">撤销</button>
        <button class="control-btn" @click="checkSolution">检查答案</button>
        <button v-if="selectedBlock" class="control-btn" @click="rotateBlock">旋转</button>
      </div>

      <div class="game-board" ref="boardElement"
           @mousemove="onBoardCellMouseMove"
           @mouseleave="onBoardMouseLeave">
        <div v-for="(row, rowIndex) in board" :key="rowIndex" class="board-row">
          <div v-for="(cell, colIndex) in row"
               :key="colIndex"
               :class="['board-cell', cell.color, { 'preview': cell.preview, 'selectable': selectedBlock && !cell.blockId }]"
               @mousedown="onBoardCellMouseDown($event, rowIndex, colIndex)">
          </div>
        </div>
      </div>

      <div class="instructions" v-if="showInstructions">
        <div class="instruction-header">
          <div class="title-wrapper">
            游戏规则<span class="instruction-tip">（先选择方块，再点击游戏面板放置方块）</span>
          </div>
          <button class="close-btn" @click="toggleInstructions">×</button>
        </div>
        <div class="instruction-section">
          <div class="section-item">
            <span class="section-number">1</span>
            <div class="section-content">
              <div class="section-title">第一步：放置3个灰色基础方块</div>
              <div class="section-detail">1×1 方块 - 1×2 方块 - 1×3 方块</div>
            </div>
          </div>
          <div class="section-item">
            <span class="section-number">2</span>
            <div class="section-content">
              <div class="section-title">第二步：使用8个彩色方块填充剩余空间</div>
              <div class="block-types">
                <div class="block-type">深蓝色方块：1×4 方块 - 1×5 方块</div>
                <div class="block-type">绿色方块：2×2 方块 - 3×3 方块</div>
                <div class="block-type">红色方块：2×3 方块 - 2×4 方块</div>
                <div class="block-type">黄色方块：2×5 方块 - 4×3 方块</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 左侧面板 -->
    <div class="game-left-panel">
      <div class="panel-header">
        <button class="toggle-btn" @click="toggleLeftPanel">
          {{ showLeftPanel ? '×' : '+' }}
        </button>
      </div>
      <div v-if="showLeftPanel" class="block-section">
        <div class="section-header">
          <div class="section-title">第一步：放置灰色方块</div>
          <div class="level-preview">
            <img :src="require(`@/assets/levels/${currentLevel}.png`)" class="step-image" alt="关卡预览">
            <div v-if="selectedBlock" 
                 :class="['block', selectedBlockColor, { 'placed': blocks[selectedBlock].placed }]"
                 :style="getSelectedBlockPreviewStyle()">
            </div>
          </div>
        </div>

        <div class="blocks-container grey-blocks">
          <div v-for="(block, id) in greyBlocks" 
               :key="id"
               :class="['block', 'grey', { 'placed': block.placed, 'selected': selectedBlock === id }]"
               @click="onBlockClick(id)"
               @mousedown="onBoardCellMouseDown($event, -1, -1)"
               :data-block-id="id"
               :style="getBlockStyle(block)">
            {{ block.size[0] }}×{{ block.size[1] }}
          </div>
        </div>
      </div>
    </div>

    <!-- 右侧面板 -->
    <div class="game-right-panel">
      <div class="block-section">
        <div class="section-title">第二步：放置彩色方块</div>
        <div class="blocks-container color-blocks">
          <div v-for="(block, id) in colorBlocks" 
               :key="id"
               :class="['block', block.color, { 'placed': block.placed, 'selected': selectedBlock === id }]"
               @click="onBlockClick(id)"
               @mousedown="onBoardCellMouseDown($event, -1, -1)"
               :data-block-id="id"
               :style="getBlockStyle(block)">
            {{ block.size[0] }}×{{ block.size[1] }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { validateSolution } from '../config/levels'
import { canPlaceBlock, canPlaceBlockIgnoring } from '../utils/game'

export default {
  name: 'Home',
  computed: {
    ...mapState('game', [
      'currentLevel',
      'board',
      'selectedBlock',
      'blocks',
      'gamePhase',
      'greyBlocksPlaced'
    ]),
    ...mapGetters('game', ['greyBlocks', 'colorBlocks']),
    selectedBlockColor() {
      return this.selectedBlock ? this.blocks[this.selectedBlock].color : ''
    }
  },
  data() {
    return {
      isDragging: false,
      showInstructions: true,
      showLeftPanel: true,
    }
  },
  created() {
    this.initLevel(1)
  },
  beforeDestroy() {
    window.removeEventListener('mouseup', this.onWindowMouseUp)
  },
  methods: {
    ...mapActions('game', [
      'initLevel',
      'selectBlock',
      'placeBlock',
      'undoLastMove',
      'rotateBlock'
    ]),
    resetGame() {
      this.initLevel(this.currentLevel)
    },
    onBlockClick(blockId) {
      this.selectBlock(blockId)
    },
    canPlaceBlockHere(row, col) {
      if (!this.selectedBlock) return false
      const block = this.blocks[this.selectedBlock]
      const [height, width] = block.size
      // 已放置的方块移动时，忽略自身占位
      if (block.placed) {
        return canPlaceBlockIgnoring(this.board, row, col, height, width, this.selectedBlock)
      }
      return canPlaceBlock(this.board, row, col, height, width)
    },
    getCellFromEvent(event) {
      const boardEl = this.$refs.boardElement
      if (!boardEl) return { row: -1, col: -1 }
      const boardRect = boardEl.getBoundingClientRect()
      const padding = 8
      const cellSize = 62
      const col = Math.floor((event.clientX - boardRect.left - padding) / cellSize)
      const row = Math.floor((event.clientY - boardRect.top - padding) / cellSize)
      return { row, col }
    },
    onBoardCellMouseDown(event, row, col) {
      // 从侧边栏方块列表拖动/点击
      if (row === -1 && col === -1) {
        const blockId = event.target.getAttribute('data-block-id')
        if (blockId) {
          this.selectBlock(blockId)
        }
      } else {
        // 点击棋盘上的已放置方块 → 选中以便移动
        const cell = this.board[row][col]
        if (cell.blockId) {
          this.selectBlock(cell.blockId)
        }
      }
      if (this.selectedBlock) {
        this.isDragging = true
        window.addEventListener('mouseup', this.onWindowMouseUp)
      }
    },
    onBoardCellMouseMove(event) {
      if (!this.isDragging || !this.selectedBlock) return

      const { row, col } = this.getCellFromEvent(event)
      if (row >= 0 && row < 8 && col >= 0 && col < 8 && this.canPlaceBlockHere(row, col)) {
        this.$store.commit('game/SET_BOARD_PREVIEW', { row, col, blockId: this.selectedBlock })
      } else {
        this.$store.commit('game/CLEAR_BOARD_PREVIEW')
      }
    },
    onWindowMouseUp(event) {
      window.removeEventListener('mouseup', this.onWindowMouseUp)
      this.onBoardCellMouseUp(event)
    },
    onBoardCellMouseUp(event) {
      if (!this.isDragging || !this.selectedBlock) return

      this.$store.commit('game/CLEAR_BOARD_PREVIEW')

      const { row, col } = this.getCellFromEvent(event)
      if (row >= 0 && row < 8 && col >= 0 && col < 8 && this.canPlaceBlockHere(row, col)) {
        this.placeBlock({ row, col, blockId: this.selectedBlock })
      }
      // 放置后自动取消选中，防止重复放置
      this.$store.commit('game/SET_SELECTED_BLOCK', null)

      this.isDragging = false
    },
    checkSolution() {
      const result = validateSolution(this.board)
      if (result.valid) {
        this.$message.success(result.message)
      } else {
        this.$message.warning(result.message)
      }
    },
    prevLevel() {
      if (this.currentLevel > 1) {
        this.initLevel(this.currentLevel - 1)
      }
    },
    nextLevel() {
      if (this.currentLevel < 88) {
        this.initLevel(this.currentLevel + 1)
      }
    },
    showLevelInput() {
      const level = prompt('请输入关卡数 (1-88):', this.currentLevel)
      const number = parseInt(level, 10)
      if (!isNaN(number) && number >= 1 && number <= 88) {
        this.initLevel(number)
      } else {
        this.$message.warning('请输入1到88之间的数字')
      }
    },
    onBoardMouseLeave() {
      if (this.isDragging) {
        this.$store.commit('game/CLEAR_BOARD_PREVIEW')
      }
    },
    getBlockStyle(block) {
      const baseSize = 62
      return {
        width: `${block.size[1] * baseSize}px`,
        height: `${block.size[0] * baseSize}px`
      }
    },
    getSelectedBlockPreviewStyle() {
      if (!this.selectedBlock) return {}
      const block = this.blocks[this.selectedBlock]
      return {
        width: `${block.size[1] * 31}px`,
        height: `${block.size[0] * 31}px`
      }
    },
    toggleInstructions() {
      this.showInstructions = !this.showInstructions;
    },
    toggleLeftPanel() {
      this.showLeftPanel = !this.showLeftPanel;
    },
  }
}
</script>

<style scoped>
.container {
  display: grid;
  grid-template-columns: 1fr 250px 300px;
  gap: 15px;
  padding: 2px;
  max-width: 1400px;
  margin: 0 auto;
  align-items: start;
}

.game-center-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 550px;
}

.game-left-panel,
.game-right-panel {
  padding: 12px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.game-header,
.controls {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.level-display {
  display: flex;
  align-items: center;
  gap: 5px;
}

.level-text {
  color: #4CAF50;
  font-size: 20px;
  font-weight: bold;
}

.level-number {
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  padding: 0 5px;
}

.level-number:hover {
  color: #4CAF50;
}

.control-btn {
  padding: 8px 16px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  min-width: 80px;
}

.control-btn:hover {
  background-color: #45a049;
}

.game-board,
.instructions {
  width: 520px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-right: 0px;

}

.game-board {
  margin-bottom: 20px;
  padding: 8px;
  border: 1px solid #ccc;
}

.instructions {
  padding: 15px;
  margin-top: 15px;
}

.board-row {
  display: flex;
}

.board-cell {
  width: 62px;
  height: 62px;
  border: 1px solid #ddd;
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.board-cell.empty {
  background-color: #fff;
}

.board-cell.grey {
  background-color: #808080;
}

.board-cell.darkblue {
  background-color: #00008B;
}

.board-cell.green {
  background-color: #008000;
}

.board-cell.red {
  background-color: #FF0000;
}

.board-cell.yellow {
  background-color: #FFD700;
}

.board-cell.preview {
  opacity: 0.5;
}

.board-cell.selectable {
  cursor: pointer;
}

.board-cell.selectable:hover {
  background-color: rgba(76, 175, 80, 0.1);
}

.block-selector {
  width: 100%;
  max-width: 800px;
  margin: 0 auto 30px;
}

.block-section {
  margin-bottom: 15px;
  padding: 12px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.controls {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
  width: 100%;
}

.section-header {
  margin-bottom: 15px;
}

.section-title {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.level-preview {
  position: relative;
  width: 200px;
  height: 200px;
  border: 2px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.step-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.blocks-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.block {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: transform 0.2s;
  border-radius: 4px;
  font-weight: bold;
  text-shadow: 1px 1px 1px rgba(0,0,0,0.3);
}

.block:hover {
  transform: scale(1.05);
}

.block.selected {
  border: 2px solid #fff;
  box-shadow: 0 0 10px rgba(0,0,0,0.3);
  transform: scale(1.05);
}

.block.placed {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.block.grey {
  background-color: #808080;
}

.block.darkblue {
  background-color: #00008B;
}

.block.green {
  background-color: #008000;
}

.block.red {
  background-color: #FF0000;
}

.block.yellow {
  background-color: #FFD700;
}

.instruction-header {
  position: relative;
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-btn {
  background: none;
  border: none;
  color: #666;
  font-size: 24px;
  cursor: pointer;
  padding: 0 8px;
  line-height: 1;
  transition: color 0.3s;
}

.close-btn:hover {
  color: #4CAF50;
}

.instruction-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.section-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.section-number {
  width: 30px;
  height: 30px;
  background-color: #4CAF50;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.section-content {
  flex: 1;
}

.section-content .section-title {
  margin-bottom: 10px;
}

.block-types {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.block-type {
  padding: 5px 0;
  color: #666;
}

.instruction-tip {
  font-size: 14px;
  font-weight: normal;
  color: #666;
  margin-left: 8px;
  white-space: nowrap;
}

/* 创建一个包装器来包含标题和提示 */
.title-wrapper {
  display: flex;
  align-items: center;
}

/* 响应式布局 */
@media screen and (max-width: 1024px) {
  .container {
    grid-template-columns: 1fr 200px 250px;
    gap: 12px;
  }
}

@media screen and (max-width: 768px) {
  .container {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 10px;
  }
  
  .game-left-panel,
  .game-right-panel {
    padding: 8px;
  }
}

/* 更新右侧面板相关样式 */
.game-right-panel .block-section {
  padding: 12px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.game-right-panel .section-title {
  margin-bottom: 12px;
}

.game-right-panel .blocks-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.game-right-panel .block {
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  padding: 8px;
  box-sizing: border-box;
}

/* 响应式调整恢复原样 */
@media screen and (max-width: 1200px) {
  .game-right-panel .blocks-container {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
}

@media screen and (max-width: 1024px) {
  .game-right-panel .blocks-container {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  }
}

/* 更新左侧面板样式 */
.game-left-panel .block-section {
  padding: 12px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.game-left-panel .blocks-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.game-left-panel .block {
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  padding: 8px;
  box-sizing: border-box;
}

/* 添加面板头部样式 */
.panel-header {
  display: flex;
  justify-content: flex-end;
  padding: 0 10px;
  margin-bottom: 10px;
}
</style> 